import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import {
  Disclaimer,
  PointOfInterestConfig,
  PropertyInformation, SidePanelTab,
  SidePanelView,
  UpdatePreferenceResponse
} from '@store/search-board/search-board.model';
import { PreferenceElement, PreferenceGroup } from '@store/user/user.model';
import { InstanetLinkResponse, ReverseLinkResponse } from '@root/search/models/map.models';
import { PointOfInterestProperty } from '@root/search/models/map.constants';
import { select, Store } from '@ngrx/store';
import { selectSidePanelState } from '@store/search-board/search-board.selector';
import { ReportsMixPanelUtil } from '@root/reports/reports-mixpanel-util.module';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { AppState } from '@root/app-state.module';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { map } from 'rxjs/operators';
import { google } from '@shared/models/googlemaps.model';
import LatLngLiteral = google.maps.LatLngLiteral;

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);
  public disclaimerMap: Map<string, Array<Disclaimer>> = new Map();

  constructor(private readonly httpClient: HttpClient,
              private readonly mixpanelService: MixpanelService,
              private readonly store$: Store<AppState>) {
  }

  public loadPointOfInterestConfig(): Observable<Array<PointOfInterestConfig>> {
    const pointsOfInterest = [];
    for (const enumValue in PointOfInterestProperty) {
      if (PointOfInterestProperty.hasOwnProperty(enumValue)) {
        pointsOfInterest.push({
          key: enumValue,
          label: PointOfInterestProperty[enumValue]
        } as PointOfInterestConfig);
      }
    }
    return of(pointsOfInterest);
  }

  public getReverseLink(preferenceGroupDto: PreferenceGroup, propertyInformation: PropertyInformation): Observable<ReverseLinkResponse> {
    return this.httpClient.post<ReverseLinkResponse>('/api/reverselinkURL', {
      preferenceGroupDto,
      propertyInformation
    });
  }

  public getReverseLinkGrid(
    preferenceGroupDto: PreferenceGroup,
    propertyInformation: PropertyInformation
  ): Observable<ReverseLinkResponse> {
    return this.httpClient.post<ReverseLinkResponse>('/api/reverselinkURL-grid', {
      preferenceGroupDto,
      propertyInformation
    });
  }

  public getInstanetLink(preferenceGroupDto: PreferenceGroup, propertyInformation: PropertyInformation): Observable<InstanetLinkResponse> {
    this.mixPanelServiceTracking('Transaction Desk Click', propertyInformation.propertyData.PARCEL_ID);
    return this.httpClient.post<InstanetLinkResponse>('/api/instanetLinkURL', {
      preferenceGroupDto,
      propertyInformation
    });
  }

  public getRegionCoordinates(): Observable<Array<LatLngLiteral>> {
    return this.httpClient.get<Array<LatLngLiteral>>('/api/preference/get-region-geocodes');
  }

  public updateMapPreferences(preferences: Array<PreferenceElement>): Observable<UpdatePreferenceResponse> {
    return this.httpClient.post<UpdatePreferenceResponse>('/api/preference/update-map-preferences', preferences);
  }

  public loadDisclaimer(latLong: { latitude: number; longitude: number }): Observable<{disclaimer: Disclaimer[], latLong: string }> {
    const key = JSON.stringify(latLong);

    if (this.disclaimerMap.has(key)) {
      return of({disclaimer: this.disclaimerMap.get(key), latLong: key})
    } else {
      return this.httpClient.get<Disclaimer[]>('/api/map/get-parcel-map-disclaimer', { params: latLong })
        .pipe(map((data) => {
          this.disclaimerMap.set(key, data);
          return {
            disclaimer: data,
            latLong: key
          }
        }));
    }

  }

  private mixPanelServiceTracking(eventId: string, propertyId: string) {
    this.store$
      .pipe(
        select(selectSidePanelState)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });

    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    setTimeout( () => {
      this.mixpanelService.track(eventId, {
        propertyIdentifier: propertyId,
        countyList: this.searchMixpanelUtil.countyList,
        searchType: this.sidePanelTabOpened,
        currentPage: this.searchMixpanelUtil.currentPageName
      });
    }, ReportsMixPanelUtil.TIME_DELAY_FOR_MIXPANEL );

  }
}
